/**
 * @fileoverview My queue section component.
 * @summary Displays current user's songs in queue.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/singer/components/MyQueueSection
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMusic } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import type { FC } from 'react'
import type { QueueItem } from '../../../store/slices/queueSlice'
import Spinner from '../../../components/Spinner'
import QueueItemRow from './QueueItemRow'

interface MyQueueSectionProps {
  items: QueueItem[]
  isLoading: boolean
  currentPseudo: string
}

/**
 * @summary My queue section component.
 * @param {MyQueueSectionProps} props - Component props.
 * @returns {ReactElement} My queue section component.
 */
const MyQueueSection: FC<MyQueueSectionProps> = ({ items, isLoading, currentPseudo }) => {
  const { t } = useTranslation()

  return (
    <div className="card mb-6">
      <h2 className="text-lg font-semibold text-(--color-text-primary) mb-4 flex items-center gap-2">
        <FontAwesomeIcon icon={faMusic} />
        {t('singer.myQueue')}
      </h2>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <p className="text-(--color-text-secondary) text-center py-4">
          {t('singer.noSongsInQueue')}
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <QueueItemRow key={item.id} item={item} currentPseudo={currentPseudo} />
          ))}
        </div>
      )}
    </div>
  )
}

export default MyQueueSection

